
import React, { useState } from 'react';
import { login, register, resetPassword } from '../services/authService';
import { User } from '../types';
import { Sparkles, ArrowRight, Loader2, KeyRound, Mail, CheckCircle2, MessageCircle, PartyPopper, AlertCircle, Info } from 'lucide-react';

interface Props {
  onLogin: (user: User) => void;
}

const Auth: React.FC<Props> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [registeredUser, setRegisteredUser] = useState<User | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      if (isRecovering) {
        await resetPassword(email);
        setSuccessMsg("Link enviado! Verifique seu e-mail (inclusive a pasta SPAM ou Lixo Eletrônico).");
      } else if (isRegistering) {
        if (!password) {
          setError("Por favor, defina uma senha.");
          setLoading(false);
          return;
        }
        const user = await register(email, password);
        setRegisteredUser(user);
      } else {
        if (!password) {
          setError("Senha é obrigatória para o login.");
          setLoading(false);
          return;
        }
        const user = await login(email, password);
        onLogin(user);
      }
    } catch (err: any) {
      console.error("Auth Error:", err);
      let msg = "Erro ao processar solicitação. Tente novamente.";
      
      // Tradução de erros comuns do Firebase para o usuário
      switch (err.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
          msg = "Email ou senha incorretos. Verifique os dados.";
          break;
        case 'auth/user-not-found':
          msg = "Usuário não encontrado em nossa base.";
          break;
        case 'auth/email-already-in-use':
          msg = "Este e-mail já está em uso por outra conta.";
          break;
        case 'auth/weak-password':
          msg = "A senha é muito fraca. Use pelo menos 6 caracteres.";
          break;
        case 'auth/invalid-email':
          msg = "O formato do e-mail inserido é inválido.";
          break;
        case 'auth/unauthorized-domain':
          msg = "Erro técnico: Domínio não autorizado no Firebase. Adicione este endereço no console do projeto.";
          break;
        case 'auth/too-many-requests':
          msg = "Muitas tentativas falhas. Tente novamente em alguns minutos.";
          break;
        default:
          msg = err.message || msg;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleRecovery = () => {
    setIsRecovering(!isRecovering);
    setError(null);
    setSuccessMsg(null);
    setRegisteredUser(null);
  };

  if (registeredUser) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 animate-fade-in">
        <div className="w-full max-w-lg bg-chroma-card border border-emerald-500/30 rounded-3xl shadow-2xl p-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
          
          <div className="text-center space-y-8 relative z-10">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 mb-2">
              <PartyPopper size={48} />
            </div>
            
            <h2 className="text-3xl font-bold text-chroma-text">Conta Criada! 🚀</h2>
            
            <div className="space-y-6 text-base leading-relaxed text-chroma-text opacity-90">
              <p>Seja bem-vindo(a) ao Portal João Caputo. Sua conta gratuita já está ativa!</p>
              
              <div className="bg-emerald-900/10 border border-emerald-500/20 p-6 rounded-2xl text-emerald-700 dark:text-emerald-200 text-sm text-left leading-relaxed">
                <p className="font-bold mb-3 flex items-center gap-3 text-base">
                  <CheckCircle2 size={20} /> ATIVAÇÃO DE PLANO:
                </p>
                Se você adquiriu um plano <strong>Bronze, Prata ou Ouro</strong>, nossa equipe realizará a ativação manual após a confirmação do pagamento.
              </div>

              <a 
                href="https://wa.me/5511988721416?text=Olá! Acabei de me cadastrar no portal e gostaria de agilizar a ativação do meu plano."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-black rounded-2xl transition-all shadow-lg text-lg"
              >
                <MessageCircle size={22} />
                Agilizar no WhatsApp
              </a>
            </div>

            <button 
              onClick={() => onLogin(registeredUser)}
              className="w-full py-4 border-2 border-purple-200 dark:border-purple-800 rounded-2xl text-purple-600 dark:text-purple-300 font-bold hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all text-base"
            >
              Entrar no Portal agora
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-chroma-card border border-purple-800/50 rounded-3xl shadow-2xl p-10 relative overflow-hidden">
        
        <div className="absolute top-0 right-0 w-32 h-32 bg-chroma-accent/10 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>

        <div className="text-center mb-10 relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-tr from-chroma-accent to-purple-700 mb-6 shadow-xl shadow-purple-500/30 transform rotate-3">
            {isRecovering ? <KeyRound className="text-white w-10 h-10" /> : <Sparkles className="text-white w-10 h-10" />}
          </div>
          <h1 className="text-3xl font-bold text-chroma-text mb-2">
            {isRecovering ? 'Recuperar Senha' : isRegistering ? 'Criar Conta' : 'Acesso ao Portal'}
          </h1>
          <p className="text-chroma-muted text-sm font-bold tracking-widest uppercase">
            Metodologia João Caputo
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
          <div>
            <label className="block text-sm font-bold text-purple-600 dark:text-purple-300 uppercase mb-2 ml-1 tracking-wider">E-mail</label>
            <div className="relative">
              <Mail className="absolute left-4 top-4 text-purple-500" size={18} />
              <input 
                type="email" 
                required
                className="w-full bg-purple-50 dark:bg-[#130521] border border-purple-200 dark:border-purple-800 rounded-xl pl-12 pr-4 py-4 text-chroma-text focus:border-chroma-accent focus:ring-2 focus:ring-chroma-accent/20 outline-none transition-all text-base"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          {!isRecovering && (
            <div>
              <label className="block text-sm font-bold text-purple-600 dark:text-purple-300 uppercase mb-2 ml-1 tracking-wider">Senha</label>
              <input 
                type="password" 
                required={!isRecovering}
                className="w-full bg-purple-50 dark:bg-[#130521] border border-purple-200 dark:border-purple-800 rounded-xl px-4 py-4 text-chroma-text focus:border-chroma-accent focus:ring-2 focus:ring-chroma-accent/20 outline-none transition-all text-base"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {!isRegistering && (
                <div className="flex justify-end mt-2">
                  <button 
                    type="button"
                    onClick={toggleRecovery}
                    className="text-xs font-bold text-purple-500 hover:text-chroma-accent transition-colors"
                  >
                    Esqueceu a senha?
                  </button>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-red-900/10 border border-red-500/30 text-red-600 dark:text-red-400 text-sm font-bold flex items-start gap-3">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="p-4 rounded-xl bg-emerald-900/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-sm font-bold flex items-start gap-3">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}
          
          <button 
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-chroma-accent hover:from-purple-500 hover:to-fuchsia-400 text-white font-black py-4.5 rounded-2xl transition-all shadow-xl hover:shadow-purple-500/25 flex items-center justify-center gap-3 group disabled:opacity-70 disabled:cursor-not-allowed text-lg"
          >
            {loading ? <Loader2 className="animate-spin" size={24} /> : (
              <>
                {isRecovering ? 'ENVIAR LINK' : isRegistering ? 'CRIAR CONTA' : 'ACESSAR PORTAL'}
                <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center space-y-4">
          {isRecovering ? (
            <button 
              onClick={toggleRecovery}
              className="text-purple-500 dark:text-purple-400 hover:text-chroma-accent font-bold text-sm transition-colors flex items-center justify-center gap-2 mx-auto"
            >
              <ArrowRight size={14} className="rotate-180" /> Voltar ao login
            </button>
          ) : (
            <button 
              onClick={() => { setIsRegistering(!isRegistering); setError(null); setSuccessMsg(null); }}
              className="text-purple-500 dark:text-purple-400 hover:text-chroma-accent font-bold text-sm transition-colors"
            >
              {isRegistering ? 'Já tem conta? Fazer login' : 'Não tem conta? Começar Gratuitamente'}
            </button>
          )}
        </div>

        <div className="mt-8 p-4 bg-blue-500/5 border border-blue-500/10 rounded-2xl flex gap-3">
           <Info className="text-blue-400 shrink-0" size={16} />
           <p className="text-[10px] text-blue-300 leading-relaxed font-medium">
             <strong>Atenção:</strong> Se você não estiver recebendo e-mails, verifique sua pasta de SPAM. O sistema de login usa o banco de dados oficial Caputo V2.0.
           </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
